import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button as MUIButton,
    Tabs,
    Tab,
    styled,
    alpha,
    Checkbox,
    Chip,
} from "@mui/material";
import {
    useNotify,
    useTranslate,
    usePermissions,
} from "react-admin";
import { useEffect, useState, useMemo} from "react";
import { SimpleTreeView, TreeItem, treeItemClasses } from "@mui/x-tree-view";
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import IndeterminateCheckBoxRoundedIcon from '@mui/icons-material/IndeterminateCheckBoxRounded';
import { Resources } from "../resource";
import PermissionPicker from "../../components/PermissionPicker";
import { flattenPermissionGroups } from "../../utils/permission-menu-groups";
import useRoles from "../roles/useRole";
import useUsers from "./useUser";
import { UserModel } from "../../entities/user.entity";
import { Permission, PermissionData } from "../../entities/permission.entity";
import { hasPermission } from "../../utils/has-permission";
import { Role } from "../../entities/role.entity";
import { Iconify } from "src/components/iconify";

const AssignRolePopup = (props: any) => {
    const { id, onClose } = props;
    const { data } = usePermissions<PermissionData>();
    const { permissions = [] } = data || {};

    const t = useTranslate();
    const notify = useNotify();
    const { roles, getListRoles, listPermissions, getListPermissions } = useRoles();
    const { getUser, assignRoleToUser, assignPermissionToUser } = useUsers();
    const [user, setUser] = useState<UserModel>();
    const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
    const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
    const [rolePermissionIds, setRolePermissionIds] = useState<string[]>([]);
    // Same tree the Roles screen uses, so both screens group permissions identically.
    const flatPermissions = useMemo(() => flattenPermissionGroups(listPermissions), [listPermissions]);
    const [hasAssignRole, setHasAssignRole] = useState(false);
    const [hasAssignPermission, setHasAssignPermission] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        setHasAssignRole(hasPermission(permissions, "users", "assign-role"));
        setHasAssignPermission(hasPermission(permissions, "users", "assign-permission"));
    }, [permissions]);

    useEffect(() => {
        if (hasAssignRole) getListRoles();
        if (hasAssignPermission) getListPermissions();
        getUser(id).then((res: any) => setUser(res.data));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAssignRole, hasAssignPermission]);

    useEffect(() => {
        if (user && roles) {
            const userRoles = new Set(user.roles || []);
            const matched: string[] = [];
            const collect = (list: Role[]) => {
                list.forEach((r) => {
                    if (userRoles.has(r.name)) matched.push(r.id);
                    if (r.childrens) collect(r.childrens);
                });
            };
            collect(roles);
            setSelectedRoleIds(matched);
        }
    }, [user, roles]);

    useEffect(() => {
        if (user && listPermissions && hasAssignPermission) {
            const matched: string[] = [];
            listPermissions.forEach((group: any) =>
                group.resources.forEach((resource: any) =>
                    resource.actions.forEach((per: any) => {
                        const isMatched = user.permissions?.some(
                            (rp: Permission) =>
                                rp.resource === per.resource && rp.action === per.action
                        );
                        if (isMatched && per.id) matched.push(per.id);
                    })
                )
            );
            setSelectedPermissionIds(matched);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, listPermissions]);

    useEffect(() => {
        if (selectedRoleIds && listPermissions) {
            const matched: string[] = [];
            listPermissions.forEach((group: any) =>
                group.resources.forEach((resource: any) =>
                    resource.actions.forEach((per: any) => {
                        const isMatched = per.roles?.some((r: Role) =>
                            selectedRoleIds.includes(r.id)
                        );
                        if (isMatched && per.id) matched.push(per.id);
                    })
                )
            );
            setRolePermissionIds(matched);
        }
    }, [selectedRoleIds, listPermissions]);

    const handleToggleRole = (id: string, checked: boolean) =>
        setSelectedRoleIds((prev) =>
            checked ? [...prev, id] : prev.filter((i) => i !== id)
        );


    const handleSubmit = async () => {
        setIsSyncing(true);
        try {
            if (hasAssignRole) await assignRoleToUser(id, selectedRoleIds);
            if (hasAssignPermission) await assignPermissionToUser(id, selectedPermissionIds);
            notify("Assigned Successfully", { type: "success" });
            onClose();
        } catch (error: any) {
            notify(error?.response?.data?.message || "Error", { type: "error" });
        }
        setIsSyncing(false);
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 2 }}>
                {t('resource.roles')} & {t('resource.permissions')}
                <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
                    {(hasAssignRole && hasAssignPermission) && <Tab label={t('resource.roles')} />}
                    {hasAssignPermission && <Tab label={t('resource.permissions')} />}
                </Tabs>
            </DialogTitle>

            <DialogContent>
                {tabIndex === 0 && hasAssignRole && (
                    <SimpleTreeView sx={{
                                        height: '60vh',
                                        overflowY: 'auto',
                                        flexGrow: 1,
                                    }} slots={{ expandIcon: ExpandIcon, collapseIcon: CollapseIcon, endIcon: EndIcon }}>
                        {renderRoleTree(transformRolesToTreeItems(roles), handleToggleRole, selectedRoleIds)}
                    </SimpleTreeView>
                )}
                {tabIndex === 1 && hasAssignPermission && (
                    <PermissionPicker
                        permissions={flatPermissions}
                        selectedIds={selectedPermissionIds}
                        onSelectedIdsChange={setSelectedPermissionIds}
                        lockedIds={rolePermissionIds}
                        height="60vh"
                        renderActionSuffix={(permission) =>
                            rolePermissionIds.includes(permission.id) ? (
                                <Chip
                                    size="small"
                                    variant="soft"
                                    color="info"
                                    label={t('resource.roles')}
                                    sx={{ ml: 1, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
                                />
                            ) : null
                        }
                    />
                )}
            </DialogContent>
            <DialogActions>
                <MUIButton onClick={onClose}>{t("actions.cancel")}</MUIButton>
                {(hasAssignRole || hasAssignPermission) && (
                    <MUIButton
                        variant="contained"
                        startIcon={<Iconify icon="eva:save-fill" />}
                        disabled={isSyncing}
                        onClick={handleSubmit}
                    >
                        {t("ra.action.save")}
                    </MUIButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

interface TreeItemNode {
    id: string;
    label: string;
    children?: TreeItemNode[];
}

const transformRolesToTreeItems = (roles: Role[]): TreeItemNode[] => {
    return roles.map((role) => ({
        id: role.id,
        label: role.name,
        children: role.childrens && role.childrens.length > 0 ? transformRolesToTreeItems(role.childrens) : undefined,
    }));
};

const renderRoleTree = (items: any[], toggle: any, selected: string[]) =>
    items.map((item) => (
        <CustomTreeItem
            key={item.id}
            itemId={item.id}
            label={
                <Box display="flex" alignItems="center">
                    <Checkbox
                        checked={selected.includes(item.id)}
                        onChange={(e) => toggle(item.id, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                    />
                    {item.label}
                </Box>
            }
        >
            {item.children && renderRoleTree(item.children, toggle, selected)}
        </CustomTreeItem>
    ));


const CustomTreeItem = styled(TreeItem)(({ theme }) => ({
    [`& .${treeItemClasses.content}`]: { padding: theme.spacing(0.5, 1) },
    [`& .${treeItemClasses.groupTransition}`]: {
        marginLeft: 15,
        paddingLeft: 18,
        borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
    },
}));

const ExpandIcon = (props: any) => <AddBoxRoundedIcon {...props} sx={{ opacity: 0.8 }} />;
const CollapseIcon = (props: any) => <IndeterminateCheckBoxRoundedIcon {...props} sx={{ opacity: 0.8 }} />;
const EndIcon = (props: any) => <Box {...props} sx={{ width: 0 }} />;

export default AssignRolePopup;